import type {RequirementOf} from './types';
import {effectivePositionMode} from './position-mode';
import {assertNever} from './assert-never';

/**
 * Elements that carry a size in their own config *and* in the shared base geometry:
 * `PhotoRenderer` and `LogoRenderer` read one or the other depending on the position
 * mode, so the toolbar has to follow suit. Signature and QR size differently — see
 * the note on {@link resolveElementSize}.
 */
export type SizedElement = RequirementOf<'photo'> | RequirementOf<'logo'>;

export type ElementSize = {width: number; height: number};

/** Declared as a type alias, not an interface, so a patch is assignable to the
 *  `Record<string, unknown>` config-patch callbacks without a cast. */
export type SizePatch = Partial<ElementSize>;

/** Size an unsized photo falls back to, in px. Shared with `PhotoRenderer`. */
export const PHOTO_DEFAULT_SIZE: ElementSize = {width: 200, height: 200};

/**
 * The floor under a text answer's box, in px — a MINIMUM, so content is still
 * the real height and nothing is ever clipped.
 *
 * **One rule, because there were three.** The same authored text element was
 * drawn at three different heights: the editor's cell view at 24/32 (`h-6` /
 * `min-h-[32px]`), a standalone element's read-only box at 28/48 (`h-7` /
 * `min-h-[48px]`), and the report's real control at whatever its padding and
 * `rows={2}` came to. So a JAC laid out in the editor grew by roughly 10px per
 * text cell on the report, and every table holding them pushed into whatever
 * was placed below it. The last of the cell-scale defaults to be reconciled:
 * `CELL_IMAGE_DEFAULT_HEIGHT` and `CELL_SIGNATURE_DEFAULT_HEIGHT` got there
 * first, and their story is the same one — a default written on one side of a
 * seam is a default the other side disagrees with.
 *
 * **These numbers are the REPORT's, and the direction is deliberate.** They are
 * what the fill control already occupies: `text-xs` is a 12px font on a 16px
 * line, so one line inside `px-2 py-1` and a 1px border is 26, and a
 * `rows={2}` textarea inside `p-1` and the same border is 42. Stating them
 * changes nothing about the job report — every filled report and every PDF
 * renders exactly as it did — and brings the editor to it. That way round
 * because the report IS the document: what a client signs cannot shift to suit
 * the design surface, and an author who lays a table out against a shorter box
 * than the one that prints has been told the wrong thing about their own page.
 * The editor's tables get taller, which is the overflow the report always had
 * and the editor was hiding.
 *
 * No cell/page distinction, unlike its two neighbours above: a text box's
 * height comes from its type and its padding, and neither depends on whether a
 * `<td>` or a page is holding it.
 *
 * **`fontSize` is not optional dressing.** The numbers above are a 12px font on
 * a 16px line; a cell that sets its own type needs its own floor, or the box
 * stays at 42 while two rows of 18pt need 58 and the answer is clipped by the
 * layout rather than by the textarea. Derived rather than tabulated: a line box
 * is about 4/3 of its font size, and the padding and border are the same either
 * way.
 */
export function textBoxMinHeight(opts: {singleLine: boolean; fontSize?: number}): number {
  const line = Math.round((opts.fontSize ?? 12) * (4 / 3));
  /** `py-1`/`p-1` top and bottom, plus a 1px border on each side. */
  const frame = 10;
  return (opts.singleLine ? line : line * 2) + frame;
}

/**
 * How tall this photo slot is, for the burned stamp's glyph to be chosen from.
 *
 * One function because two things read it and they must not disagree: the
 * capture sends it to the API (`slot_height`, which picks the glyph — see
 * `label_font_px` there), and `PhotoStampOverlay` previews the glyph that choice
 * produces. A preview drawn from a different height than the upload sent would
 * promise a stamp the burn does not make, which is the whole failure the
 * generated geometry table exists to prevent.
 *
 * The AUTHORED height, defaulted the way the canvas defaults it — not the box as
 * measured. Measured pixels are canvas units: the preview is drawn at a zoom and
 * `FittedCanvas` scales it again, so a height read off the DOM would depend on
 * how far the author had zoomed out when the crew happened to take the photo.
 *
 * Known nuance: `square`, and a slot with no width, DRAW at their column rather
 * than at the stored height, so for those the two genuinely differ. The authored
 * number still decides, because it is the only one that is stable and the only
 * one an author can see.
 */
export function slotHeightPx(cfg: {height?: number}): number {
  return cfg.height ?? PHOTO_DEFAULT_SIZE.height;
}

/** Size an unsized logo falls back to, in px — `LogoRenderer`'s crop frame. */
export const LOGO_DEFAULT_SIZE: ElementSize = {width: 200, height: 150};

const DEFAULT_SIZE: Record<SizedElement['type'], ElementSize> = {
  photo: PHOTO_DEFAULT_SIZE,
  logo: LOGO_DEFAULT_SIZE,
};

/**
 * The element's size as the canvas renders it, in px.
 *
 * An **absolute** element is laid out from the base geometry — `x`/`y`/`width`/`height`
 * on `RequirementBase`, the same fields drag-resize writes — and its renderer then
 * fills that box at `100%`, ignoring `config.width`/`config.height` outright. A **flow**
 * element is the reverse: no base box, sized entirely by its config. An absolute
 * element that was never sized falls back to its flow size before the type's default,
 * so the first number typed is an adjustment rather than a jump.
 *
 * This is the one definition of that chain, and the renderers read it too, so the
 * number in the toolbar cannot disagree with the box on the page (DERP-226 items 3
 * and 3b: the inputs used to write the config while absolute, which nothing reads).
 */
export function resolveElementSize(element: SizedElement): ElementSize {
  const cfg = element.config;
  const defaults = DEFAULT_SIZE[element.type];
  if (effectivePositionMode(element) === 'absolute') {
    return {
      width: element.width ?? cfg.width ?? defaults.width,
      height: element.height ?? cfg.height ?? defaults.height,
    };
  }
  return {width: cfg.width ?? defaults.width, height: cfg.height ?? defaults.height};
}

/**
 * Write a size back to whichever pair the element is laid out from, leaving the other
 * pair untouched — so an element keeps its flow size across a trip through absolute
 * and back, exactly as drag-resize already leaves it.
 */
export function writeElementSize(element: SizedElement, patch: SizePatch): SizedElement {
  // Base geometry lives on the shared base, so it is written the same way for either variant.
  if (effectivePositionMode(element) === 'absolute') return {...element, ...patch};
  // Config is per-variant, so narrow before merging rather than widening the union.
  switch (element.type) {
    case 'photo':
      return {...element, config: {...element.config, ...patch}};
    case 'logo':
      return {...element, config: {...element.config, ...patch}};
    default:
      return assertNever(element, 'sized element type');
  }
}

/**
 * The CSS box for a photo, given what the author set and whether it is locked
 * to 1:1.
 *
 * One helper because four surfaces draw this box -- the fill/report renderer,
 * the editor's static cell view, the in-cell interactive stand-in, and the
 * toolbar's own reading of it -- and a square that only three of them honour is
 * worse than no square at all.
 *
 * Square is emitted as `aspectRatio`, never as a computed height. That is the
 * whole reason it works for an in-cell image input stretched to its column: the
 * width is `100%` and no stored number could say what the height should be,
 * because the column decides it at render time. `height` is dropped rather than
 * kept alongside, since a height AND a ratio is a contradiction the browser
 * resolves by ignoring one of them.
 */
export function photoBoxStyle(opts: {
  width: number | string | undefined;
  height: number | string | undefined;
  square: boolean;
  /** Emit `minHeight` rather than `height`, for the editor's cell view -- a cell
   *  must never clip its own content. Ignored when square, which has no height. */
  heightIsMinimum?: boolean;
}): {
  width?: number | string;
  height?: number | string;
  minHeight?: number | string;
  aspectRatio?: string;
} {
  const {width, height, square, heightIsMinimum} = opts;
  if (square) return {width, aspectRatio: '1 / 1'};
  if (heightIsMinimum) return {width, minHeight: height};
  return {width, height};
}

/**
 * The size patch a W/H edit becomes, honouring a 1:1 lock.
 *
 * While square, a typed width carries the height with it -- the ratio is what
 * renders, but keeping the number in step is what makes switching square OFF
 * land on the box the author was just looking at rather than an older one.
 */
export function squareAwareSizePatch(patch: SizePatch, square: boolean): SizePatch {
  if (!square) return patch;
  if (patch.width !== undefined) return {width: patch.width, height: patch.width};
  if (patch.height !== undefined) return {width: patch.height, height: patch.height};
  return patch;
}

/**
 * Turning the 1:1 lock on or off.
 *
 * On: **the width wins** -- the height snaps to match it. The width is the axis
 * an author reads first in a page layout and the one the drag handle sets, so it
 * is the dimension they were most recently choosing. When there is no width to
 * win (an in-cell image input left to stretch to its column) the height is left
 * alone: the ratio renders regardless, and the stored number is what switching
 * the lock back off will restore.
 */
export function toggleSquarePatch(
  cfg: {width?: number; height?: number},
  next: boolean,
): {square: boolean; height?: number} {
  if (!next) return {square: false};
  return cfg.width === undefined ? {square: true} : {square: true, height: cfg.width};
}
